export interface MapWorkbenchPopupIdentityFeatureLike {
  id?: string | number | null;
  sourceLayer?: string | null;
  layerId?: string | null;
  geometryType?: string | null;
  properties?: Record<string, unknown> | null;
}

export interface MapWorkbenchPopupIdentitySummaryRow {
  key: string;
  label: string;
  value: string;
}

const TITLE_FIELDS = [
  'name',
  'title',
  'label',
  'display_name',
  'site_name',
  'location_name',
  'address',
  'ticket_number',
  'incident_number',
  'account_name',
];

const SUBTITLE_FIELDS = [
  'subtitle',
  'display_subtitle',
  'secondary_label',
  'type',
  'category',
  'status',
  'priority',
  'severity',
];

const stringifyFieldValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

const selectFirstFieldValue = (
  properties: Record<string, unknown>,
  fields: string[],
  exclude = new Set<string>(),
): string | null => {
  for (const field of fields) {
    if (exclude.has(field)) {
      continue;
    }
    const value = stringifyFieldValue(properties[field]);
    if (value) {
      return value;
    }
  }
  return null;
};

const selectFirstScalarValue = (properties: Record<string, unknown>): string | null => {
  for (const value of Object.values(properties)) {
    const stringValue = stringifyFieldValue(value);
    if (stringValue) {
      return stringValue;
    }
  }
  return null;
};

export interface MapWorkbenchResolvedPopupIdentity {
  title: string;
  subtitle: string | null;
}

export const resolveMapWorkbenchPopupIdentity = ({
  feature,
  authoredTitle,
  authoredSubtitle,
  summary,
}: {
  feature: MapWorkbenchPopupIdentityFeatureLike;
  authoredTitle?: string | null;
  authoredSubtitle?: string | null;
  summary?: MapWorkbenchPopupIdentitySummaryRow[];
}): MapWorkbenchResolvedPopupIdentity => {
  const properties = feature.properties ?? {};
  const title = authoredTitle?.trim();
  const subtitle = authoredSubtitle?.trim();
  const heuristicTitle = selectFirstFieldValue(properties, TITLE_FIELDS);
  const heuristicSubtitle = selectFirstFieldValue(properties, SUBTITLE_FIELDS);
  const summaryValue = summary?.find(item => item.value?.trim())?.value?.trim() ?? null;
  const sourceLabel = feature.sourceLayer ?? feature.layerId ?? null;
  const idLabel = feature.id != null ? `ID ${String(feature.id)}` : null;
  const geometryLabel = feature.geometryType ? `${feature.geometryType}` : null;

  const resolvedTitle =
    title ||
    heuristicTitle ||
    summaryValue ||
    selectFirstScalarValue(properties) ||
    (idLabel ? `Feature ${String(feature.id)}` : sourceLabel ?? geometryLabel ?? 'Visible feature');

  const resolvedSubtitle =
    subtitle ||
    heuristicSubtitle ||
    [sourceLabel, idLabel].filter((value): value is string => Boolean(value)).join(' · ') ||
    geometryLabel ||
    null;

  return {
    title: resolvedTitle,
    subtitle: resolvedSubtitle && resolvedSubtitle !== resolvedTitle ? resolvedSubtitle : null,
  };
};
